import { Badge } from "@/components/ui/badge";
import { Check, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { TagFollowButton } from "./TagFollowButton";
import { useAuth } from "@/hooks/useAuth";

interface PopularTag {
  tag_name: string;
  count: number;
}

interface PopularBookTagsProps {
  tags: PopularTag[];
  userTags: string[];
  onAddTag: (tag: string) => void;
  disabled?: boolean;
}

export function PopularBookTags({
  tags,
  userTags,
  onAddTag,
  disabled = false,
}: PopularBookTagsProps) {
  const { user } = useAuth();
  
  if (tags.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <TrendingUp className="h-4 w-4 text-primary" />
        <span>Popular Tags</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map(({ tag_name, count }) => {
          const isUserTag = userTags.includes(tag_name);

          return (
            <div key={tag_name} className="flex items-center gap-1">
              <button
                onClick={() => !isUserTag && onAddTag(tag_name)}
                disabled={disabled || isUserTag}
                className={cn(
                  "group transition-all",
                  isUserTag && "cursor-default"
                )}
              >
                <Badge
                  variant={isUserTag ? "default" : "outline"}
                  className={cn(
                    "pl-3 pr-3 py-1.5 text-sm",
                    !isUserTag && "hover:bg-primary hover:text-primary-foreground cursor-pointer"
                  )}
                >
                  <span className="flex items-center gap-2">
                    {tag_name}
                    <span className="text-xs opacity-70">• {count}</span>
                    {isUserTag && <Check className="h-3 w-3 ml-1" />}
                  </span>
                </Badge>
              </button>
              {user && count >= 3 && (
                <TagFollowButton
                  tagName={tag_name}
                  variant="ghost"
                  size="icon"
                  showLabel={false}
                />
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground">
        Click a tag to add it to your list
      </p>
    </div>
  );
}
